// MobileMessagesPage — Iter62c — Dark Premium iOS + FUNCTIONAL + WhatsApp long-press
// Fixes: proper safe-area padding, ASA logo watermark background,
// long-press context menu (Répondre/Transférer/Épingler/Réactions/Supprimer),
// reply preview bar, pinned banner, reactions.

import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatRelative } from '../constants/users';
import { NotifBell } from '../components/AppLayout';
import { ClientTreeView } from '../components/ClientTreeView';

const T = {
  bg: '#0B1330',
  surface: '#141D42',
  surface2: '#1C2650',
  hairline: 'rgba(255,255,255,0.09)',
  hairlineSoft: 'rgba(255,255,255,0.05)',
  text: '#F5F7FB',
  textMuted: '#8A93B5',
  textFaint: '#565F84',
  blue: '#3E7BFA',
  blueSoft: 'rgba(62,123,250,0.16)',
  blueLight: '#7FADFF',
  blueMid: '#4C8DFF',
  blueDeep: '#2A56D6',
  blueNavy: '#1E3A8A',
  danger: '#F0616B',
};
const FONT = `-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Inter', sans-serif`;
const LOGO_URL = '/asa-logo.png';

// Iter 62cq — Auto-link URLs in messages (mobile). Matches http(s)://… and www.…
const URL_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
function Linkified({ text, mine }) {
  if (!text) return null;
  const parts = String(text).split(URL_RE);
  return (
    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {parts.map((part, i) => {
        if (i % 2 === 1) {
          const href = part.startsWith('http') ? part : `https://${part}`;
          return (
            <a
              key={i}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              style={{
                color: mine ? '#fff' : T.blueLight,
                textDecoration: 'underline',
                textUnderlineOffset: 2,
                wordBreak: 'break-all',
              }}
            >{part}</a>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
}

// ---- helpers -------------------------------------------------------------
function colorForKey(key) {
  const palette = [
    `linear-gradient(135deg, ${T.blueMid}, ${T.blueDeep})`,
    T.blueNavy, T.blueDeep,
    `linear-gradient(135deg, ${T.blueLight}, ${T.blueMid})`,
    T.blueMid,
  ];
  let h = 0;
  for (let i = 0; i < (key || '').length; i++) h = (h * 31 + key.charCodeAt(i)) & 0xffffff;
  return palette[h % palette.length];
}
function initialsFromName(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

// Shared ambient watermark for all screens.
function AmbientLayer() {
  return (
    <>
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      }}>
        <img src={LOGO_URL} alt="" style={{ width: 320, opacity: 0.06, filter: 'drop-shadow(0 0 40px rgba(62,123,250,0.4))' }} />
      </div>
      <div style={{
        position: 'absolute', top: -60, right: -80, width: 280, height: 280,
        background: 'radial-gradient(circle, rgba(127,173,255,0.4) 0%, rgba(62,123,250,0.14) 45%, transparent 70%)',
        filter: 'blur(14px)', pointerEvents: 'none', zIndex: 0,
      }} />
    </>
  );
}

// ============================================================================
// Root page
// ============================================================================
export default function MobileMessagesPage() {
  const { user } = useAuth();
  // Iter 62bv — Founder OR gestionnaire can create groups
  const isFounder = user?.role === 'founder' || user?.role === 'gestionnaire';
  const [convs, setConvs] = useState([]);
  const [users, setUsers] = useState([]);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [communities, setCommunities] = useState([]);
  const [selectedCommunity, setSelectedCommunity] = useState('all');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter 62ae — Vue arborescente Client → Projet. Défaut : arbre pour monteurs.
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('asa_msg_view_mode');
    if (saved === 'tree' || saved === 'flat') return saved;
    return isFounder ? 'flat' : 'tree';
  });
  useEffect(() => { localStorage.setItem('asa_msg_view_mode', viewMode); }, [viewMode]);

  const reload = useCallback(async () => {
    try {
      const [c, co, u] = await Promise.all([
        api.get('/conversations'),
        api.get('/communities').catch(() => ({ data: [] })),
        api.get('/users').catch(() => ({ data: [] })),
      ]);
      setConvs(c.data || []);
      setCommunities(co.data || []);
      setUsers((u.data || []).filter((x) => x.id !== user?.id));
    } catch { /* ignore */ }
    setLoading(false);
  }, [user?.id]);
  useEffect(() => { reload(); }, [reload]);

  const items = useMemo(() => {
    let list = [...convs];
    if (selectedCommunity !== 'all') list = list.filter((c) => c.community_id === selectedCommunity);
    if (filter === 'dm') list = list.filter((c) => c.type === 'dm');
    else if (filter === 'groups') list = list.filter((c) => c.type === 'group');
    else if (filter === 'unread') list = list.filter((c) => (c.unread_count || 0) > 0);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((c) => (c.name || '').toLowerCase().includes(q) || (c.last?.text || '').toLowerCase().includes(q));
    return list.sort((a, b) => (b.last?.created_at || b.updated_at || '').localeCompare(a.last?.created_at || a.updated_at || ''));
  }, [convs, selectedCommunity, filter, search]);

  const groupsCount = useMemo(() => convs.filter((c) => c.type === 'group').length, [convs]);
  const unreadCount = useMemo(() => convs.reduce((s, c) => s + (c.unread_count || 0), 0), [convs]);

  if (active) {
    return (
      <ConvoScreen
        conv={active}
        currentUserId={user?.id}
        onBack={() => { setActive(null); reload(); }}
      />
    );
  }

  return (
    <div
      data-testid="mobile-messages-page"
      style={{
        minHeight: '100dvh',
        background: T.bg, color: T.text, fontFamily: FONT,
        position: 'relative', overflowX: 'hidden',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 90px)',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <AmbientLayer />

      <div style={{
        position: 'relative', zIndex: 2,
        padding: 'calc(env(safe-area-inset-top, 0px) + 14px) 24px 4px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ fontSize: 11, color: T.textFaint, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Elevate Influence
        </div>
        <NotifBell placement="ios" />
      </div>

      <div style={{ position: 'relative', zIndex: 2, padding: '10px 24px 12px' }}>
        <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 14 }}>Discussions</div>
        {/* Iter 62ae — View toggle */}
        <div
          data-testid="mobile-msg-view-toggle"
          style={{
            display: 'inline-flex', gap: 2, padding: 3, borderRadius: 999,
            background: T.surface, border: `1px solid ${T.hairline}`,
            marginBottom: 12,
          }}
        >
          <button
            data-testid="mobile-msg-view-tree"
            onClick={() => setViewMode('tree')}
            style={{
              padding: '7px 14px', borderRadius: 999, border: 'none',
              background: viewMode === 'tree' ? 'rgba(245,197,106,0.18)' : 'transparent',
              color: viewMode === 'tree' ? '#F5C56A' : T.textMuted,
              fontFamily: FONT, fontWeight: 700, fontSize: 12, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >Clients</button>
          <button
            data-testid="mobile-msg-view-flat"
            onClick={() => setViewMode('flat')}
            style={{
              padding: '7px 14px', borderRadius: 999, border: 'none',
              background: viewMode === 'flat' ? T.blueSoft : 'transparent',
              color: viewMode === 'flat' ? T.blueLight : T.textMuted,
              fontFamily: FONT, fontWeight: 700, fontSize: 12, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >Toutes</button>
        </div>
        {viewMode === 'flat' && <SearchBar value={search} onChange={setSearch} />}
      </div>

      {viewMode === 'tree' ? (
        <div style={{ position: 'relative', zIndex: 2, padding: '0 20px 20px' }}>
          <ClientTreeView
            isMobile
            onOpenConversation={(conv) => setActive(conv)}
          />
        </div>
      ) : (
      <>
      <div style={{ padding: '0 24px 16px', position: 'relative', zIndex: 2 }}>
        <div style={{ display: 'flex', gap: 14, overflowX: 'auto', scrollbarWidth: 'none' }}>
          <CommunityDot
            label="Tous" letters="Tous" active={selectedCommunity === 'all'}
            bg={`linear-gradient(135deg, ${T.blueLight}, ${T.blueDeep})`}
            onClick={() => setSelectedCommunity('all')}
          />
          {communities.map((co) => (
            <CommunityDot
              key={co.id}
              label={co.name}
              letters={(co.name || '').slice(0, 2).toUpperCase()}
              active={selectedCommunity === co.id}
              bg={T.surface2}
              onClick={() => setSelectedCommunity(co.id)}
            />
          ))}
        </div>
      </div>

      <div style={{ padding: '0 24px 16px', position: 'relative', zIndex: 2 }}>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none' }}>
          <Pill active={filter === 'all'} onClick={() => setFilter('all')}>Toutes</Pill>
          <Pill active={filter === 'groups'} onClick={() => setFilter('groups')} count={groupsCount || undefined}>Groupes</Pill>
          <Pill active={filter === 'dm'} onClick={() => setFilter('dm')}>DM</Pill>
          <Pill active={filter === 'unread'} onClick={() => setFilter('unread')} count={unreadCount || undefined}>Non lues</Pill>
        </div>
      </div>

      <div style={{ position: 'relative', zIndex: 2, padding: '0 24px' }}>
        {loading ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: T.textFaint, fontSize: 13 }}>Chargement…</div>
        ) : items.length === 0 ? (
          <div style={{
            padding: '60px 20px', textAlign: 'center',
            color: T.textFaint, fontSize: 13,
            background: T.surface, borderRadius: 16,
            border: `1px dashed ${T.hairline}`,
          }}>
            {search ? 'Aucun résultat' : 'Aucune discussion'}
          </div>
        ) : (
          <div>
            {items.map((c) => (
              <ChatRow key={c.id} conv={c} currentUserId={user?.id} onClick={() => setActive(c)} />
            ))}
          </div>
        )}
      </div>
      </>
      )}

      {/* Iter 62bv — FAB "+" to open the mobile new-group creator (founder + gestionnaire) */}
      {isFounder && (
        <button
          data-testid="mobile-msg-new-group-fab"
          onClick={() => setShowNewGroup(true)}
          aria-label="Nouveau groupe"
          style={{
            position: 'fixed', zIndex: 60,
            right: 20,
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 100px)',
            width: 56, height: 56, borderRadius: 28,
            border: 'none', cursor: 'pointer',
            background: `linear-gradient(140deg, ${T.blueMid}, ${T.blueDeep})`,
            color: '#fff', fontSize: 30, fontWeight: 300,
            display: 'grid', placeItems: 'center',
            boxShadow: '0 12px 32px rgba(62,123,250,0.45), 0 2px 6px rgba(0,0,0,0.4)',
            transition: 'transform 0.15s',
          }}
          onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.92)'; }}
          onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          +
        </button>
      )}

      {showNewGroup && (
        <MobileNewGroupModal
          users={users}
          onClose={() => setShowNewGroup(false)}
          onCreated={() => { setShowNewGroup(false); reload(); toast.success('Groupe créé'); }}
        />
      )}
    </div>
  );
}

// ============================================================================
// UI atoms
// ============================================================================
function SearchBar({ value, onChange }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      background: T.surface, border: `1px solid ${T.hairline}`,
      borderRadius: 14, padding: '12px 14px',
    }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
        <circle cx="11" cy="11" r="7" stroke={T.textFaint} strokeWidth="1.6" />
        <path d="M20 20L16.5 16.5" stroke={T.textFaint} strokeWidth="1.6" strokeLinecap="round" />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Rechercher une discussion"
        data-testid="msg-search-input"
        style={{
          flex: 1, background: 'transparent', border: 'none', outline: 'none',
          color: T.text, fontSize: 13.5, fontFamily: FONT,
        }}
      />
    </div>
  );
}

function CommunityDot({ label, letters, active, bg, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`community-dot-${label}`}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
        flexShrink: 0, background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: FONT,
      }}
    >
      <div style={{
        position: 'relative', width: 48, height: 48, borderRadius: 16, background: bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 800, color: active || bg !== T.surface2 ? '#fff' : T.textMuted,
      }}>
        {letters}
        {active && (
          <span style={{ position: 'absolute', inset: -3, borderRadius: 18, border: `2px solid ${T.blueLight}` }} />
        )}
      </div>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: T.textMuted }}>{label}</div>
    </button>
  );
}

function Pill({ children, active, onClick, count }) {
  return (
    <button
      onClick={onClick}
      data-testid={`msg-filter-${String(children).toLowerCase()}`}
      style={{
        flexShrink: 0, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
        color: active ? '#fff' : T.textMuted,
        background: active ? `linear-gradient(135deg, ${T.blueMid}, ${T.blueDeep})` : T.surface,
        padding: '8px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 6, fontFamily: FONT,
      }}
    >
      {children}
      {count != null && (
        <span style={{
          fontSize: 10, fontWeight: 700,
          background: active ? 'rgba(255,255,255,0.25)' : T.surface2,
          color: active ? '#fff' : T.textFaint,
          padding: '1px 6px', borderRadius: 8,
        }}>{count}</span>
      )}
    </button>
  );
}

// ============================================================================
// Chat row
// ============================================================================
function ChatRow({ conv, currentUserId, onClick }) {
  const isGroup = conv.type === 'group';
  const other = !isGroup ? conv.with : null;
  const displayName = isGroup ? conv.name : (other?.name || 'Sans nom');
  const preview = conv.last?.text || '—';
  const time = conv.last?.created_at ? formatRelative(conv.last.created_at) : '';
  const unread = conv.unread_count || 0;
  const mine = conv.last?.from === currentUserId;
  const avatarBg = isGroup ? colorForKey(conv.id) : (other?.color || colorForKey(other?.id || displayName));

  let senderLabel = null;
  if (isGroup && conv.last?.from) {
    const s = (conv.members || []).find((m) => (typeof m === 'string' ? m : m.id) === conv.last.from);
    if (s && typeof s !== 'string') senderLabel = s.name?.split(' ')[0];
  }

  return (
    <button
      onClick={onClick}
      data-testid={`chat-row-${conv.id}`}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 4px', textAlign: 'left',
        background: 'transparent', border: 'none', cursor: 'pointer',
        borderBottom: `1px solid ${T.hairlineSoft}`,
        color: T.text, fontFamily: FONT,
      }}
    >
      <div style={{
        width: 50, height: 50, borderRadius: '50%', background: avatarBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 15, fontWeight: 800, color: '#fff', flexShrink: 0, overflow: 'hidden',
      }}>
        {isGroup ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M17 20V18C17 15.8 15.2 14 13 14H7C4.8 14 3 15.8 3 18V20" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
            <circle cx="10" cy="8" r="3.5" stroke="white" strokeWidth="1.8"/>
            <path d="M21 20V18.5C21 16.8 19.8 15.4 18.2 15.1" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
            <path d="M15.5 6.2C16.9 6.6 18 7.9 18 9.5C18 11.1 16.9 12.4 15.5 12.8" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        ) : other?.avatar_url ? (
          <img src={other.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          initialsFromName(displayName)[0]
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
          <div style={{
            fontSize: 14.5, fontWeight: 700, color: T.text,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{displayName}</div>
          <div style={{ fontSize: 11, color: unread > 0 ? T.blueLight : T.textFaint, fontWeight: 600, marginLeft: 8, flexShrink: 0 }}>
            {time}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <div style={{
            fontSize: 12.5, color: T.textMuted, flex: 1,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {senderLabel && <span style={{ color: T.textFaint, fontWeight: 600 }}>{senderLabel} : </span>}
            {preview}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {unread > 0 ? (
              <span style={{
                minWidth: 19, height: 19, borderRadius: 10, background: T.blue, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10.5, fontWeight: 700, padding: '0 5px',
              }}>{unread}</span>
            ) : mine && conv.last ? (
              <svg width="15" height="10" viewBox="0 0 24 16" fill="none" style={{ color: T.blueLight }}>
                <path d="M1 8L5 12L13 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 8L13 12L21 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : null}
          </div>
        </div>
      </div>
    </button>
  );
}

// ============================================================================
// Conversation screen — real messages, WhatsApp long-press menu
// ============================================================================
function ConvoScreen({ conv, currentUserId, onBack }) {
  const isGroup = conv.type === 'group';
  const other = !isGroup ? conv.with : null;
  const displayName = isGroup ? conv.name : (other?.name || 'Sans nom');
  const avatarBg = isGroup ? colorForKey(conv.id) : (other?.color || colorForKey(other?.id || displayName));

  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [menuMsg, setMenuMsg] = useState(null); // { m, mine } — open long-press menu
  const [replyTo, setReplyTo] = useState(null); // message object to reply to
  const scrollRef = useRef(null);
  const pressTimerRef = useRef(null);

  const load = async () => {
    try {
      const url = isGroup ? `/conversations/${conv.id}/messages` : `/messages/${other?.id}`;
      const r = await api.get(url);
      setMsgs(r.data || []);
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [conv.id]);

  const send = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const body = isGroup ? { conversation_id: conv.id, text: t } : { to: other?.id, text: t };
      if (replyTo) body.reply_to = replyTo.id;
      await api.post(isGroup ? '/conversations/message' : '/messages', body);
      setText('');
      setReplyTo(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi');
    } finally { setBusy(false); }
  };

  // Long-press handling
  const startPress = (m, mine) => {
    if (m.deleted) return;
    pressTimerRef.current = setTimeout(() => {
      if (navigator.vibrate) try { navigator.vibrate(10); } catch {/* noop */}
      setMenuMsg({ m, mine });
    }, 420);
  };
  const cancelPress = () => { if (pressTimerRef.current) { clearTimeout(pressTimerRef.current); pressTimerRef.current = null; } };

  // Actions on selected message
  const doReact = async (emoji) => {
    const m = menuMsg?.m; if (!m) return;
    setMenuMsg(null);
    try {
      await api.post(`/messages/${m.id}/reactions`, { emoji });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const doReply = () => {
    if (menuMsg?.m) setReplyTo(menuMsg.m);
    setMenuMsg(null);
  };
  const doPin = async () => {
    const m = menuMsg?.m; if (!m) return;
    setMenuMsg(null);
    try {
      await api.post(`/messages/${m.id}/pin`);
      await load();
      toast.success(m.pinned ? 'Détaché' : 'Épinglé');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const doForward = () => {
    setMenuMsg(null);
    toast('Bientôt : sélectionne une discussion');
  };
  const doDelete = async () => {
    const m = menuMsg?.m; if (!m) return;
    setMenuMsg(null);
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await api.delete(`/messages/${m.id}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const [pinnedIdx, setPinnedIdx] = useState(0);
  const pinnedMessages = useMemo(() => msgs.filter((m) => m.pinned && !m.deleted), [msgs]);
  useEffect(() => { if (pinnedIdx >= pinnedMessages.length) setPinnedIdx(0); }, [pinnedMessages.length, pinnedIdx]);
  const scrollToMsg = (mid) => {
    const el = document.querySelector(`[data-msg-id="${mid}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div
      data-testid="mobile-convo-screen"
      style={{
        position: 'fixed', inset: 0, zIndex: 60,
        display: 'flex', flexDirection: 'column',
        background: T.bg, color: T.text, fontFamily: FONT,
        WebkitTapHighlightColor: 'transparent', overflow: 'hidden',
      }}
    >
      {/* Ambient watermark behind conv (subtle) */}
      <AmbientLayer />

      {/* Header */}
      <div style={{
        position: 'relative', zIndex: 2,
        display: 'flex', alignItems: 'center', gap: 12,
        padding: 'calc(env(safe-area-inset-top, 0px) + 12px) 18px 10px',
        borderBottom: `1px solid ${T.hairlineSoft}`,
        background: 'rgba(11,19,48,0.75)', backdropFilter: 'blur(16px)',
      }}>
        <button
          onClick={onBack}
          data-testid="convo-back-btn"
          style={{
            width: 30, height: 30, borderRadius: 10,
            background: T.surface, border: 'none', color: T.blueLight,
            display: 'grid', placeItems: 'center', cursor: 'pointer', flexShrink: 0,
          }}
        >
          <svg width="8" height="13" viewBox="0 0 9 15" fill="none">
            <path d="M8 1L1 7.5L8 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div style={{
          width: 38, height: 38, borderRadius: '50%', background: avatarBg, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 800, flexShrink: 0, overflow: 'hidden',
        }}>
          {isGroup ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="10" cy="8" r="3.5" stroke="white" strokeWidth="1.8"/>
              <path d="M17 20V18C17 15.8 15.2 14 13 14H7C4.8 14 3 15.8 3 18V20" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          ) : other?.avatar_url ? (
            <img src={other.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            initialsFromName(displayName)[0]
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 15, fontWeight: 700, color: T.text,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{displayName}</div>
          <div style={{ fontSize: 11, color: T.textFaint }}>
            {isGroup ? `${(conv.members || []).length} membres` : (other?.role === 'founder' ? 'Founder' : other?.role === 'gestionnaire' ? 'Gestionnaire' : 'Editor')}
          </div>
        </div>
      </div>

      {/* Pinned messages banner — cyclable through all pinned messages */}
      {pinnedMessages.length > 0 && (
        <div style={{
          position: 'relative', zIndex: 2,
          padding: '8px 18px',
          background: T.surface, borderBottom: `1px solid ${T.hairlineSoft}`,
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.textMuted,
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L14 8L20 9L15.5 13L17 20L12 16.5L7 20L8.5 13L4 9L10 8L12 2Z" stroke={T.blueLight} strokeWidth="1.6" strokeLinejoin="round"/>
          </svg>
          <button
            onClick={() => {
              const next = (pinnedIdx + 1) % pinnedMessages.length;
              scrollToMsg(pinnedMessages[pinnedIdx].id);
              setPinnedIdx(next);
            }}
            data-testid="pinned-banner-jump"
            style={{
              flex: 1, background: 'transparent', border: 'none', textAlign: 'left',
              color: T.textMuted, fontSize: 12, cursor: 'pointer',
              overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
            }}
          >
            <span style={{ color: T.blueLight, fontWeight: 700, marginRight: 6 }}>
              {pinnedIdx + 1}/{pinnedMessages.length}
            </span>
            {pinnedMessages[pinnedIdx]?.text}
          </button>
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        data-testid="convo-messages"
        style={{
          flex: 1, overflowY: 'auto', position: 'relative', zIndex: 1,
          padding: '16px 18px 12px', display: 'flex', flexDirection: 'column', gap: 8,
          backgroundColor: 'transparent',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cg fill='none' stroke='%23233266' stroke-width='1.4' opacity='0.35'%3E%3Ccircle cx='20' cy='20' r='8'/%3E%3Cpath d='M60 15L64 23L73 24L66 30L68 39L60 34L52 39L54 30L47 24L56 23Z'/%3E%3Cpath d='M110 10C114 10 117 13 117 17C117 21 114 22 110 26C106 22 103 21 103 17C103 13 106 10 110 10Z'/%3E%3Crect x='10' y='70' width='16' height='12' rx='3'/%3E%3Cpath d='M60 70L76 70L76 82L68 88L68 82L60 82Z'/%3E%3Ccircle cx='115' cy='75' r='7'/%3E%3Cpath d='M12 120L28 120M20 112L20 128'/%3E%3Cpath d='M60 118C64 114 70 114 74 118C78 122 78 128 74 132C70 136 64 136 60 132C56 128 56 122 60 118Z'/%3E%3Crect x='105' y='115' width='14' height='18' rx='2'/%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '160px 160px',
        }}
      >
        {msgs.length === 0 ? (
          <div style={{ margin: 'auto', color: T.textFaint, fontSize: 12.5, textAlign: 'center' }}>
            Aucun message pour l&apos;instant
          </div>
        ) : msgs.map((m, i) => {
          if (m.deleted) return null;
          const mine = m.from === currentUserId;
          const sender = isGroup && !mine ? (conv.members || []).find((u) => (typeof u === 'string' ? u : u.id) === m.from) : null;
          const showAuthor = isGroup && !mine && (i === 0 || msgs[i - 1].from !== m.from);
          const time = m.created_at ? new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
          const reactions = m.reactions || {};
          const replyRef = m.reply_to ? msgs.find((x) => x.id === m.reply_to) : null;

          return (
            <div key={m.id} data-msg-id={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
              <div
                data-testid={`msg-${m.id}`}
                onMouseDown={() => startPress(m, mine)}
                onMouseUp={cancelPress}
                onMouseLeave={cancelPress}
                onTouchStart={() => startPress(m, mine)}
                onTouchEnd={cancelPress}
                onContextMenu={(e) => { e.preventDefault(); setMenuMsg({ m, mine }); }}
                style={{
                  maxWidth: '75%', padding: '10px 14px',
                  borderRadius: 16, fontSize: 13.5, lineHeight: 1.5,
                  background: mine ? `linear-gradient(135deg, ${T.blueMid}, ${T.blueDeep})` : T.surface,
                  color: mine ? '#fff' : T.text,
                  borderBottomLeftRadius: mine ? 16 : 4,
                  borderBottomRightRadius: mine ? 4 : 16,
                  position: 'relative', cursor: 'pointer', userSelect: 'none',
                  boxShadow: m.pinned ? `0 0 0 1.5px ${T.blueLight}55` : 'none',
                }}
              >
                {showAuthor && sender && typeof sender !== 'string' && (
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: T.blueLight, marginBottom: 3 }}>
                    {sender.name?.split(' ')[0]}
                  </div>
                )}
                {replyRef && !replyRef.deleted && (
                  <div style={{
                    borderLeft: `2px solid ${mine ? 'rgba(255,255,255,0.55)' : T.blueLight}`,
                    padding: '3px 8px', marginBottom: 5,
                    background: mine ? 'rgba(255,255,255,0.10)' : 'rgba(127,173,255,0.10)',
                    borderRadius: 4, fontSize: 11.5, opacity: 0.85,
                  }}>
                    <div style={{
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{replyRef.text}</div>
                  </div>
                )}
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}><Linkified text={m.text} mine={mine} /></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', marginTop: 4 }}>
                  {m.pinned && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2L14 8L20 9L15.5 13L17 20L12 16.5L7 20L8.5 13L4 9L10 8L12 2Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                    </svg>
                  )}
                  <span style={{ fontSize: 10, opacity: 0.6 }}>{time}</span>
                </div>
                {Object.keys(reactions).length > 0 && (
                  <div style={{
                    position: 'absolute', bottom: -10, right: 6,
                    background: T.surface2, border: `2px solid ${T.bg}`,
                    borderRadius: 12, padding: '1px 6px', fontSize: 11,
                    display: 'flex', gap: 3,
                  }}>
                    {Object.entries(reactions).slice(0, 3).map(([emoji, users]) => (
                      <span key={emoji}>{emoji}{Array.isArray(users) && users.length > 1 ? users.length : ''}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply preview above input */}
      {replyTo && (
        <div style={{
          position: 'relative', zIndex: 2,
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 16px', borderTop: `1px solid ${T.hairlineSoft}`,
          background: T.surface,
        }}>
          <div style={{ width: 3, alignSelf: 'stretch', background: T.blueLight, borderRadius: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.blueLight }}>Réponse à un message</div>
            <div style={{
              fontSize: 12, color: T.textMuted,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{replyTo.text}</div>
          </div>
          <button
            onClick={() => setReplyTo(null)}
            data-testid="cancel-reply"
            style={{ background: 'transparent', border: 'none', color: T.textFaint, cursor: 'pointer', padding: 4 }}
          >✕</button>
        </div>
      )}

      {/* Input */}
      <form
        onSubmit={send}
        style={{
          position: 'relative', zIndex: 2, flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px calc(env(safe-area-inset-bottom, 0px) + 12px)',
          borderTop: `1px solid ${T.hairlineSoft}`,
          background: 'rgba(11,19,48,0.92)', backdropFilter: 'blur(16px)',
        }}
      >
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Message..."
          data-testid="convo-input"
          style={{
            flex: 1, background: T.surface,
            border: `1px solid ${T.hairline}`, borderRadius: 20,
            padding: '10px 16px', color: T.text, fontSize: 13.5,
            fontFamily: FONT, outline: 'none',
          }}
        />
        <button
          type="submit"
          disabled={!text.trim() || busy}
          data-testid="convo-send-btn"
          style={{
            width: 38, height: 38, borderRadius: '50%',
            background: `linear-gradient(135deg, ${T.blueLight}, ${T.blueDeep})`,
            border: 'none', color: '#fff',
            display: 'grid', placeItems: 'center', cursor: 'pointer',
            opacity: (!text.trim() || busy) ? 0.55 : 1, flexShrink: 0,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M3 11L21 3L13 21L11 13L3 11Z" stroke="white" strokeWidth="1.8" strokeLinejoin="round"/>
          </svg>
        </button>
      </form>

      {/* Long-press context menu */}
      {menuMsg && (
        <LongPressMenu
          onClose={() => setMenuMsg(null)}
          onReact={doReact}
          onReply={doReply}
          onForward={doForward}
          onPin={doPin}
          onDelete={doDelete}
          canDelete={menuMsg.mine}
          pinned={menuMsg.m.pinned}
        />
      )}
    </div>
  );
}

// ============================================================================
// Long-press context menu (WhatsApp-style)
// ============================================================================
function LongPressMenu({ onClose, onReact, onReply, onForward, onPin, onDelete, canDelete, pinned }) {
  return (
    <div
      onClick={onClose}
      data-testid="msg-longpress-menu"
      style={{
        position: 'absolute', inset: 0, zIndex: 30,
        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute', left: 20, right: 20, top: '38%',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}
      >
        {/* Reactions bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-around',
          background: T.surface, borderRadius: 26, padding: '10px 14px',
          boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
        }}>
          {['👍', '❤️', '😂', '😮', '😢', '🙏'].map((e) => (
            <button
              key={e}
              onClick={() => onReact(e)}
              data-testid={`react-${e}`}
              style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer' }}
            >{e}</button>
          ))}
        </div>
        {/* Actions list */}
        <div style={{
          background: 'linear-gradient(160deg, #1B2856, #101A3C)',
          borderRadius: 16, overflow: 'hidden',
          boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
        }}>
          <ActionItem label="Répondre" icon="reply" onClick={onReply} testid="action-reply" />
          <ActionItem label="Transférer" icon="forward" onClick={onForward} testid="action-forward" />
          <ActionItem label={pinned ? 'Détacher' : 'Épingler'} icon="pin" onClick={onPin} testid="action-pin" />
          {canDelete && <ActionItem label="Supprimer" icon="trash" onClick={onDelete} danger testid="action-delete" />}
        </div>
      </div>
    </div>
  );
}

function ActionItem({ label, icon, onClick, danger, testid }) {
  const color = danger ? T.danger : T.textMuted;
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
        padding: '13px 16px', fontSize: 13.5, fontWeight: 600,
        background: 'transparent', border: 'none',
        borderBottom: `1px solid ${T.hairlineSoft}`, textAlign: 'left',
        color: danger ? T.danger : T.text, cursor: 'pointer', fontFamily: FONT,
      }}
    >
      {icon === 'reply' && (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path d="M9 10L4 15L9 20M4 15H16C18.2 15 20 13.2 20 11V6" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
      {icon === 'forward' && (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path d="M15 10L20 15L15 20M20 15H8C5.8 15 4 13.2 4 11V6" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
      {icon === 'pin' && (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path d="M6 3H18V21L12 17.5L6 21V3Z" stroke={color} strokeWidth="1.7" strokeLinejoin="round"/>
        </svg>
      )}
      {icon === 'trash' && (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path d="M3 6H21M8 6V4C8 3 9 2 10 2H14C15 2 16 3 16 4V6M19 6V20C19 21 18 22 17 22H7C6 22 5 21 5 20V6" stroke={color} strokeWidth="1.7" strokeLinecap="round"/>
        </svg>
      )}
      {label}
    </button>
  );
}


// ============================================================================
// Iter 62bv — MobileNewGroupModal : create a group without a client
// ============================================================================
function MobileNewGroupModal({ users, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [members, setMembers] = useState({});
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setMembers((s) => ({ ...s, [id]: !s[id] }));
  const memberIds = useMemo(() => Object.keys(members).filter((k) => members[k]), [members]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) { toast.error('Nom du groupe requis'); return; }
    setBusy(true);
    try {
      await api.post('/conversations/group', {
        name: name.trim(), members: memberIds,
      });
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(3,7,18,0.75)', backdropFilter: 'blur(10px)',
        display: 'flex', alignItems: 'flex-end',
      }}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="mobile-new-group-modal"
        style={{
          width: '100%', maxHeight: '90vh',
          background: T.surface, borderTopLeftRadius: 24, borderTopRightRadius: 24,
          border: `1px solid ${T.hairline}`, padding: 22,
          display: 'flex', flexDirection: 'column', gap: 14,
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
        }}
      >
        <div style={{
          width: 36, height: 4, background: T.hairline, borderRadius: 2,
          alignSelf: 'center', marginBottom: 4,
        }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: T.text }}>Nouveau groupe</div>
          <button
            type="button" onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: T.textFaint, fontSize: 22, cursor: 'pointer' }}
          >×</button>
        </div>

        <div>
          <div style={{ fontSize: 11, color: T.textFaint, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
            Nom du groupe
          </div>
          <input
            data-testid="mobile-group-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex: Team Nash · Sprint mars"
            style={{
              width: '100%', padding: '12px 14px', borderRadius: 12,
              background: T.surface2, border: `1px solid ${T.hairline}`, color: T.text,
              fontSize: 15, fontFamily: FONT, outline: 'none',
            }}
          />
        </div>

        <div style={{ flex: 1, overflow: 'auto', maxHeight: '50vh' }}>
          <div style={{ fontSize: 11, color: T.textFaint, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
            Membres ({memberIds.length})
            <span style={{ color: T.textFaint, fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: 8 }}>
              — tu seras auto-ajouté
            </span>
          </div>
          {users.length === 0 && (
            <div style={{ color: T.textFaint, fontSize: 13, padding: 12, textAlign: 'center' }}>
              Aucun autre utilisateur
            </div>
          )}
          {users.map((u) => {
            const active = !!members[u.id];
            return (
              <button
                type="button"
                key={u.id}
                onClick={() => toggle(u.id)}
                data-testid={`mobile-group-member-${u.id}`}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 12px', borderRadius: 12, marginBottom: 6,
                  background: active ? T.blueSoft : T.surface2,
                  border: `1.5px solid ${active ? T.blueMid : T.hairline}`,
                  color: T.text, fontFamily: FONT, textAlign: 'left', cursor: 'pointer',
                }}
              >
                <div style={{
                  width: 34, height: 34, borderRadius: 17,
                  background: u.color || T.blueMid,
                  display: 'grid', placeItems: 'center',
                  fontWeight: 700, fontSize: 12, color: '#fff',
                }}>
                  {initialsFromName(u.name)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{u.name}</div>
                  <div style={{ fontSize: 11, color: T.textFaint }}>
                    {u.role === 'founder' ? 'Founder' : u.role === 'gestionnaire' ? 'Gestionnaire' : u.role === 'client' ? 'Client' : 'Editor'}
                  </div>
                </div>
                <div style={{
                  width: 22, height: 22, borderRadius: 11,
                  border: `2px solid ${active ? T.blueMid : T.hairline}`,
                  background: active ? T.blueMid : 'transparent',
                  display: 'grid', placeItems: 'center',
                }}>
                  {active && <span style={{ color: '#fff', fontSize: 12 }}>✓</span>}
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1, padding: '13px 14px', borderRadius: 14,
              background: T.surface2, border: `1px solid ${T.hairline}`, color: T.textMuted,
              fontFamily: FONT, fontWeight: 600, fontSize: 14, cursor: 'pointer',
            }}
          >Annuler</button>
          <button
            type="submit"
            data-testid="mobile-group-submit"
            disabled={busy || !name.trim()}
            style={{
              flex: 1, padding: '13px 14px', borderRadius: 14,
              background: (!name.trim() || busy) ? T.textFaint : `linear-gradient(140deg, ${T.blueMid}, ${T.blueDeep})`,
              border: 'none', color: '#fff',
              fontFamily: FONT, fontWeight: 700, fontSize: 14,
              cursor: (!name.trim() || busy) ? 'not-allowed' : 'pointer',
              opacity: (!name.trim() || busy) ? 0.6 : 1,
            }}
          >{busy ? '…' : 'Valider'}</button>
        </div>
      </form>
    </div>
  );
}

